import { useState } from 'react'
import {
  Brush,
  ChevronsUpDown,
  Languages,
  LogOut,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar'
import { SignOutDialog } from '@/components/sign-out-dialog'
import { useSession } from '@/shared/hooks/useSession'

type OperatorsProfileDropdownProps = {
  variant?: 'default' | 'sidebar'
}

function getInitials(firstName?: string, lastName?: string): string {
  const parts = [firstName?.[0], lastName?.[0]].filter(Boolean).join('')
  return parts.toUpperCase() || 'U'
}

function useAccountPreferencesBasePath(): string {
  const { installationId } = useParams<{ installationId: string }>()
  if (!installationId) {
    return '/dashboard'
  }
  return `/dashboard/${installationId}/account`
}

function OperatorsUserMenuItems({
  onSignOut,
  onNavigate,
}: {
  onSignOut: () => void
  onNavigate: () => void
}) {
  const { t } = useTranslation('common')
  const accountBase = useAccountPreferencesBasePath()

  return (
    <>
      <DropdownMenuSeparator />
      <DropdownMenuGroup>
        <DropdownMenuItem asChild>
          <Link
            to={`${accountBase}/theme`}
            onClick={() => {
              onNavigate()
            }}
          >
            <Brush className="size-4 shrink-0" />
            {t('settings.theme')}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link
            to={`${accountBase}/language`}
            onClick={() => {
              onNavigate()
            }}
          >
            <Languages className="size-4 shrink-0" />
            {t('settings.language')}
          </Link>
        </DropdownMenuItem>
      </DropdownMenuGroup>
      <DropdownMenuSeparator />
      <DropdownMenuItem
        variant="destructive"
        onClick={() => {
          onSignOut()
        }}
      >
        <LogOut className="size-4 shrink-0" />
        {t('profile.signOut')}
      </DropdownMenuItem>
    </>
  )
}

function OperatorsProfileDropdownSidebar() {
  const { isMobile, setOpenMobile } = useSidebar()
  const [signOutOpen, setSignOutOpen] = useState(false)
  const { session } = useSession()

  const fullName = [session.firstName, session.lastName].filter(Boolean).join(' ')
  const displayName = fullName || session.email || 'User'
  const email = session.email || ''
  const initials = getInitials(session.firstName, session.lastName)

  function handleNavigate() {
    if (isMobile) {
      setOpenMobile(false)
    }
  }

  return (
    <>
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                size="lg"
                className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
              >
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarFallback className="rounded-lg">{initials}</AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-start text-sm leading-tight">
                  <span className="truncate font-semibold">{displayName}</span>
                  <span className="truncate text-xs">{email}</span>
                </div>
                <ChevronsUpDown className="ms-auto size-4 shrink-0" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="min-w-56 rounded-lg"
              side={isMobile ? 'bottom' : 'right'}
              align="end"
              sideOffset={4}
            >
              <DropdownMenuLabel className="p-0 font-normal">
                <div className="flex items-center gap-2 px-1 py-1.5 text-start text-sm">
                  <Avatar className="h-8 w-8 rounded-lg">
                    <AvatarFallback className="rounded-lg">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-start text-sm leading-tight">
                    <span className="truncate font-semibold">{displayName}</span>
                    <span className="truncate text-xs">{email}</span>
                  </div>
                </div>
              </DropdownMenuLabel>
              <OperatorsUserMenuItems
                onSignOut={() => setSignOutOpen(true)}
                onNavigate={handleNavigate}
              />
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>
      <SignOutDialog open={signOutOpen} onOpenChange={setSignOutOpen} />
    </>
  )
}

function OperatorsProfileDropdownDefault() {
  const [signOutOpen, setSignOutOpen] = useState(false)
  const { session } = useSession()

  const fullName = [session.firstName, session.lastName].filter(Boolean).join(' ')
  const displayName = fullName || session.email || 'User'
  const email = session.email || ''
  const initials = getInitials(session.firstName, session.lastName)

  return (
    <>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="relative h-8 w-8 rounded-full p-0">
            <Avatar className="h-8 w-8">
              <AvatarFallback>{initials}</AvatarFallback>
            </Avatar>
            <span className="sr-only">{displayName}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56" align="end" forceMount>
          <DropdownMenuLabel className="font-normal">
            <div className="flex flex-col gap-1.5">
              <p className="text-sm leading-none font-medium">{displayName}</p>
              <p className="text-xs leading-none text-muted-foreground">
                {email}
              </p>
            </div>
          </DropdownMenuLabel>
          <OperatorsUserMenuItems
            onSignOut={() => setSignOutOpen(true)}
            onNavigate={() => {}}
          />
        </DropdownMenuContent>
      </DropdownMenu>
      <SignOutDialog open={signOutOpen} onOpenChange={setSignOutOpen} />
    </>
  )
}

export function OperatorsProfileDropdown({
  variant = 'default',
}: OperatorsProfileDropdownProps) {
  if (variant === 'sidebar') {
    return <OperatorsProfileDropdownSidebar />
  }
  return <OperatorsProfileDropdownDefault />
}
